import React, { useState, useEffect, memo, useMemo } from 'react';
import { List } from 'lucide-react';
import { useThrottle, useSmoothScroll } from '@/hooks/usePerformanceOptimization';
import { useKeyboardNavigation, AccessibleButton, ScreenReaderAnnouncer } from './AccessibilityUtils';

interface TOCItem {
  id: string;
  title: string;
  level: number;
}

interface TableOfContentsProps {
  content: string;
  className?: string;
}

export const TableOfContents: React.FC<TableOfContentsProps> = memo(({ content, className = '' }) => {
  // Performance optimized
  const [activeId, setActiveId] = useState<string>('');
  const [announceMessage, setAnnounceMessage] = useState<string>('');
  const { scrollToElement } = useSmoothScroll();

  // Memoize TOC generation for better performance
  const tocItems = useMemo(() => {
    const headingRegex = /^(#{1,6})\s+(.+)$/gm;
    const items: TOCItem[] = [];
    let match;

    while ((match = headingRegex.exec(content)) !== null) {
      const level = match[1].length;
      const title = match[2].trim();
      const id = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      
      items.push({ id, title, level });
    }

    return items;
  }, [content]);

  // Throttle scroll handler for better performance
  const throttledScrollHandler = useThrottle(() => {
    const headings = tocItems.map(item => document.getElementById(item.id)).filter(Boolean);
    
    for (let i = headings.length - 1; i >= 0; i--) {
      const heading = headings[i];
      if (heading && heading.getBoundingClientRect().top <= 100) {
        setActiveId(heading.id);
        break;
      }
    }
  }, 16);

  // Track active section
  useEffect(() => {
    window.addEventListener('scroll', throttledScrollHandler, { passive: true });
    throttledScrollHandler(); // Initial check
    
    return () => window.removeEventListener('scroll', throttledScrollHandler);
  }, [throttledScrollHandler]);

  const scrollToHeading = (id: string) => {
    const item = tocItems.find(item => item.id === id);
    if (item) {
      scrollToElement(id, 120);
      setAnnounceMessage(`Navigated to ${item.title}`);
      setTimeout(() => setAnnounceMessage(''), 1000);
    }
  };

  // Keyboard navigation for TOC items
  const { activeIndex } = useKeyboardNavigation(
    tocItems.map(item => item.id),
    scrollToHeading
  );

  if (tocItems.length === 0) {
    return null;
  }

  return (
    <>
      <nav 
        className={`
          bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6
          ${className}
        `}
        role="navigation"
        aria-label="Table of contents"
      >
        <div className="flex items-center gap-3 mb-4">
          <List className="w-5 h-5 text-blue-400" aria-hidden="true" />
          <h3 className="text-white font-semibold">Table of Contents</h3>
        </div>
        
        <ul className="space-y-2" role="list">
          {tocItems.map((item, index) => (
            <li key={item.id} role="listitem">
              <AccessibleButton
                onClick={() => scrollToHeading(item.id)}
                ariaLabel={`Navigate to ${item.title}, heading level ${item.level}`}
                className={`
                  w-full text-left px-3 py-2 rounded-lg transition-all duration-200
                  hover:bg-white/10 hover:text-blue-300
                  ${activeId === item.id 
                    ? 'bg-blue-600/20 text-blue-300 border-l-2 border-blue-400' 
                    : 'text-white/70 hover:text-white'
                  }
                  ${activeIndex === index ? 'ring-2 ring-blue-500' : ''}
                `}
                variant="ghost"
              >
                <span
                  style={{ 
                    paddingLeft: `${(item.level - 1) * 12}px`,
                    fontSize: item.level === 1 ? '0.9rem' : '0.85rem'
                  }}
                >
                  {item.title}
                </span>
              </AccessibleButton>
            </li>
          ))}
        </ul>
      </nav>
      
      {announceMessage && (
        <ScreenReaderAnnouncer message={announceMessage} priority="polite" />
      )}
    </>
  );
});

TableOfContents.displayName = 'TableOfContents';

export default TableOfContents;